<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters>
        <v-col cols="4" class="pt-1 mr-2">
          <card-title-row
            is-subtitle
            translation-path="sidebar.zdmLightingControl.title"
            :has-buttons="false"
            :has-icon-column="false"
          />
          <card-title-row
            is-subtitle
            :translation-path="`sidebar.zdmLightingControl.${isConnected ? 'connected' : 'notConnected'}`"
            :has-buttons="false"
            :has-icon-column="false"
            :title-classes="[`${isConnected ? 'success' : 'error'}--text`, 'text-caption', 'font-weight-medium']"
          />
        </v-col>
        <template v-if="isFetched">
          <v-col cols="1" class="text-left">
            <base-rounded-button
              class="px-0"
              color="rgba(235, 235, 235, 0.5)"
              icon="mdi-refresh"
              icon-color="disabled"
              size="x-small"
              :tooltip="this.$i18n.t('sidebar.zdmLightingControl.refreshData')"
              @click="init"
            />
          </v-col>
          <v-col v-if="isConnected" cols="6" class="text-left">
            <data-input v-bind="lightingLevelInput" v-model="lightingLevelValue" @input="changeLightingLevel" />
          </v-col>
        </template>
        <template v-else>
          <main-loader class="pt-3" />
        </template>
      </v-row>
      <template v-if="isFetched && isConnected">
        <v-row no-gutters align="center" class="pb-7">
          <v-col cols="auto" class="pr-1">
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <v-chip
                  label
                  color="rgba(0,0,0,0.08)"
                  text-color="font"
                  class="px-2 text-body-2 x-small"
                  style="height: 24px"
                  v-on="on"
                >
                  {{ lightingHead.providerName }}
                </v-chip>
              </template>
              {{ $i18n.t('sidebar.zdmLightingControl.providerName') }}
            </v-tooltip>
          </v-col>
          <v-col cols="auto" class="pr-2 text-body-2 font-weight-bold">
            {{ ':' }}
          </v-col>
          <v-col cols="auto" class="text-body-2">
            {{ lightingHead.headId }}
          </v-col>
        </v-row>
        <v-row no-gutters justify="center" v-if="measuredParameters.length > 0">
          <v-col cols="12">
            <sidebar-measurements-list v-bind="measurementsInput">
              <template #header.buttons>
                <base-rounded-button
                  icon="mdi-chart-line"
                  size="x-small"
                  @click="toggleHistoricalDataDialog"
                  :disabled="lightingLevelLoading"
                />
              </template>
            </sidebar-measurements-list>
          </v-col>
        </v-row>
      </template>
    </v-col>
    <zdm-lighting-head-historical-data-modal
      v-if="historicalDataDialogLoaded"
      :is-visible.sync="historicalDataDialogVisible"
      :heads-ids="[featureId]"
      :parameters="historicalDataParameters"
      is-single
    />
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import SidebarMeasurementsList from '@/components/SidebarMeasurementsList';
export default {
  name: 'ZdmLightingControlWidget',
  components: {
    CardTitleRow,
    DataInput,
    MainLoader,
    SidebarMeasurementsList,
    ZdmLightingHeadHistoricalDataModal: () => import('@/components/ZdmLightingHeadHistoricalDataModal'),
  },
  props: {
    featureId: {
      type: [Number, String],
      required: true,
    },
    values: {
      type: Object,
      required: true,
    },
  },
  computed: {
    providerAttribute: get('admin/modulesMapping@zdm_data.datasources.head.provider_attribute_name'),
    providerSystemHeadIdAttribute: get('admin/modulesMapping@zdm_data.datasources.head.provider_id_attribute_name'),
    zdmDataSourcesHead: get('admin/modulesMapping@zdm_data.datasources.head'),
    historicalDataParameters() {
      return this.measuredParameters.map(parameter => {
        const { external_name, measurement_unit, verbose_name } = parameter;
        const text = `${verbose_name}${measurement_unit ? ` [${measurement_unit}]` : ''}`;
        return { text, value: external_name };
      });
    },
    isConnected() {
      return this.isConnectionConfigurated && this.isConnectionValidate;
    },
    isConnectionConfigurated() {
      const { providerName, headId } = this.lightingHead;
      if (providerName && (headId || headId === 0)) {
        return true;
      }
      return false;
    },
    lightingLevelInput() {
      return {
        dataType: { name: 'slider' },
        isPercentageLabel: true,
        min: 0,
        max: 1,
        step: 0.01,
        titleTranslationPath: 'sidebar.zdmLightingControl.lightingLevel',
        disabled: this.lightingLevelLoading,
      };
    },
    lightingHead() {
      const headId = this.values[this.providerSystemHeadIdAttribute];
      const providerName = this.values[this.providerAttribute];
      return { headId, providerName };
    },
    measurementsInput() {
      return {
        errorTranslationPath: 'sidebar.zdmLightingControl.measurementConnectionError',
        headerTranslationPath: 'sidebar.zdmLightingControl.measuredParameters',
        measurements: this.measuredParameters,
        wrapping: true,
        loading: this.measurementsReloading,
      };
    },
  },
  data: () => ({
    historicalDataDialogVisible: false,
    historicalDataDialogLoaded: false,
    isConnectionValidate: false,
    isFetched: false,
    lightingLevelLoading: false,
    lightingLevelValue: 0,
    measuredParameters: [],
    measurementsReloading: false,
  }),
  methods: {
    checkLightHeadStatus: call('lightingControl/checkLightHeadStatus'),
    getLightHeadParameters: call('lightingControl/getLightHeadParameters'),
    lightingControlAction: call('lightingControl/lightingControlAction'),
    toggleHistoricalDataDialog() {
      this.historicalDataDialogLoaded = true;
      this.historicalDataDialogVisible = true;
    },
    async changeLightingLevel(nV) {
      this.lightingLevelLoading = true;
      this.measurementsReloading = true;
      try {
        const payload = {
          headId: this.featureId,
          action: 'brightness',
          value: { level: parseInt((nV * 100).toFixed(2)), duration: 180 },
        };
        await this.lightingControlAction(payload);

        setTimeout(async () => {
          const r = await this.getLightHeadParameters(this.featureId);
          const { measured_parameters, brightness, timestamp } = r;
          this.measuredParameters = measured_parameters.map(parameter => ({
            ...parameter,
            measurement_exists_in_system: true,
            timestamp,
          }));
          this.lightingLevelValue = parseFloat((brightness / 100).toFixed(2));
          this.lightingLevelLoading = false;
          this.measurementsReloading = false;
        }, 5000);
      } catch {
        this.lightingLevelLoading = false;
      }
    },
    async init() {
      this.isFetched = false;
      if (!this.isConnectionConfigurated) {
        this.isConnectionValidate = false;
        this.measuredParameters = [];
        this.lightingLevelValue = 0;
        this.isFetched = true;
        return;
      }
      try {
        const r = await this.checkLightHeadStatus(this.featureId);
        const deviceId = r?.device?.id;
        this.isConnectionValidate = deviceId || deviceId === 0 ? true : false;
        if (this.isConnectionValidate) {
          const rr = await this.getLightHeadParameters(this.featureId);
          const { measured_parameters, brightness, timestamp } = rr;
          this.measuredParameters = measured_parameters.map(parameter => ({
            ...parameter,
            measurement_exists_in_system: true,
            timestamp,
          }));
          this.lightingLevelValue = parseFloat((brightness / 100).toFixed(2));
          this.isFetched = true;
        } else {
          this.isFetched = true;
        }
      } catch {
        this.isConnectionValidate = false;
        this.measuredParameters = [];
        this.lightingLevelValue = 0;
        this.isFetched = true;
      }
    },
  },
  watch: {
    featureId: {
      deep: true,
      immediate: true,
      handler() {
        this.init();
      },
    },
  },
  created() {
    this.changeLightingLevel = debounce(this.changeLightingLevel, 500);
  },
};
</script>
